import 'react-phone-input-2/lib/style.css';

import { FieldGroupRenderer } from '@components/forms/FieldGroupRenderer';
import { RadioCardGroup } from '@components/forms/RadioCardGroup';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@components/ui/accordion';
import { useAutoFill } from '@hooks/useAutoFill';
import { useCaseCreationSuccess, useCreateCase } from '@hooks/useCases';
import { useSchema } from '@hooks/useSchema';
import { useWorkflowsByProgram } from '@hooks/useWorkflows';
import { useStore } from '@store/index';
import { separateMetadataGroup } from '@utils/cases';
import { coerceFormDataToSchema, getDefaultValueForField, validateField } from '@utils/formSchema';
import { applyRequiredFieldsToGroups } from '@utils/schemaValidation';
import { RotateCcw, SendHorizonal } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { Breadcrumb, BreadcrumbPortal, HeaderActionsPortal } from '@/components/Header';
import { Button } from '@/components/ui/button';

// Dynamic FormData type based on field IDs
type DynamicFormData = Record<string, any>;

/**
 * Props for create case page components.
 * All workflow-specific create case pages must accept these props.
 */
export interface DefaultCreateCasePageProps {
  progId?: string;
  caseRecordId?: string | null;
}

export const DefaultCreateCasePage: React.FC<DefaultCreateCasePageProps> = ({
  progId: progIdProp,
}) => {
  const { progId: progIdParam } = useParams<{ progId: string }>();
  const progId = progIdProp || progIdParam;

  const { getSelectedWorkflow, getSelectedProgram } = useStore();
  const selectedProgram = getSelectedProgram();

  // Fetch workflows for the current program
  const { isLoading: workflowsLoading } = useWorkflowsByProgram(
    selectedProgram?.id || progId?.replace('prog-', '') || '',
  );

  const selectedWorkflow = getSelectedWorkflow();

  const createCase = useCreateCase();

  // Shared success handler
  const handleCaseCreationSuccess = useCaseCreationSuccess();

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Auto-fill hook for managing saved form data
  const { isAutoFillEnabled, hasSavedData, autoFill, saveFormData } = useAutoFill();

  // Fetch schema from schema service
  const { inputGroups, isLoading: schemaLoading } = useSchema(selectedWorkflow?.id);

  // Form state (declared before formSchema so conditional requirements can use it)
  const [formData, setFormData] = useState<DynamicFormData>({});

  // Apply conditional required fields based on current form data
  const inputGroupsWithRequired = useMemo(
    () => applyRequiredFieldsToGroups(inputGroups, formData),
    [inputGroups, formData],
  );

  // Build form schema from parsed input groups
  const formSchema = useMemo(
    () => ({ groups: inputGroupsWithRequired }),
    [inputGroupsWithRequired],
  );

  // Separate metadata group from other groups
  const { metadataGroup, otherGroups } = separateMetadataGroup(formSchema.groups);

  // Track which group is currently open - using string for Accordion value
  const [activeGroup, setActiveGroup] = useState<string>('0');

  // Reset active group when the underlying schema changes (not when form data changes)
  React.useEffect(() => {
    setActiveGroup('0');
  }, [inputGroups]);

  // Initialize form data when the underlying schema changes (not when form data changes)
  useEffect(() => {
    const initialData: DynamicFormData = {};
    inputGroups.forEach(group => {
      group.fields.forEach(field => {
        // For radio fields, default to first option if no defaultValue
        if (field.type === 'radio' && !field.defaultValue && field.options?.length) {
          initialData[field.id] = field.options[0].value;
        } else {
          // Use type-aware default (arrays → [], objects → {}, etc.)
          initialData[field.id] = getDefaultValueForField(field);
        }
      });
    });
    setFormData(initialData);
  }, [inputGroups]);

  const handleInputChange = (fieldId: string, value: any) => {
    setFormData(prev => ({ ...prev, [fieldId]: value }));
  };

  const handleInputBlur = (fieldId: string) => {
    setFormData(prev => {
      const currentValue = prev[fieldId];
      if (typeof currentValue === 'string') {
        return { ...prev, [fieldId]: currentValue.trim() };
      }
      return prev;
    });
  };

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    let hasErrors = false;
    let firstErrorGroupIndex: number | null = null;
    let firstErrorFieldId: string | null = null;
    let isFirstErrorInMetadata = false;

    // Validate metadata group first
    if (metadataGroup) {
      metadataGroup.fields.forEach(field => {
        const value = formData[field.id];
        const error = validateField(field, value);
        if (error) {
          errors[field.id] = error;
          hasErrors = true;
          if (firstErrorFieldId === null) {
            firstErrorFieldId = field.id;
            isFirstErrorInMetadata = true;
          }
        }
      });
    }

    // Validate other groups
    otherGroups.forEach((group, groupIndex) => {
      group.fields.forEach(field => {
        const value = formData[field.id];
        const error = validateField(field, value);
        if (error) {
          errors[field.id] = error;
          hasErrors = true;

          // Track the first error for focusing (only if no error found in metadata)
          if (firstErrorGroupIndex === null && !isFirstErrorInMetadata) {
            firstErrorGroupIndex = groupIndex;
            firstErrorFieldId = field.id;
          }
        }
      });
    });

    setFieldErrors(errors);

    // If there are errors, expand the accordion containing the first error (if not in metadata)
    if (hasErrors && firstErrorGroupIndex !== null && !isFirstErrorInMetadata) {
      setActiveGroup(String(firstErrorGroupIndex));
    }

    // Focus on the first error field after a short delay to allow accordion to expand
    if (hasErrors && firstErrorFieldId) {
      setTimeout(() => {
        const errorField = document.getElementById(firstErrorFieldId!);
        if (errorField) {
          errorField.focus();
          errorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 300);
    }

    return !hasErrors;
  };

  const handleAutoFill = () => {
    const savedData = autoFill();
    if (savedData) {
      setFormData(savedData);
      // Clear any existing field errors
      setFieldErrors({});
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedWorkflow) {
      toast.error('Please select a workflow first');
      return;
    }

    // Validate form using schema
    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    setIsSubmitting(true);

    try {
      // Check network connectivity before submitting
      if (!navigator.onLine) {
        toast.error('No internet connection. Please check your network and try again.');
        setIsSubmitting(false);
        return;
      }

      // Create the case using the API with a timeout promise
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Request timeout')), 35000);
      });

      const createPromise = createCase.mutateAsync({
        workflowId: selectedWorkflow.id!,
        inputData: {
          schema_version: '1.0',
          data: coerceFormDataToSchema(formData, inputGroups),
        },
      });

      // Race between the actual request and timeout
      const result = (await Promise.race([createPromise, timeoutPromise])) as any;

      if (result && result.case_record_id) {
        // Save form data to localStorage for auto-fill on next form (only for enabled workflows)
        saveFormData(formData);

        // Use shared success handler for navigation
        handleCaseCreationSuccess(result);
      }
    } catch (error: any) {
      // Error is handled by the mutation hook, but handle timeout specifically
      if (error?.message === 'Request timeout') {
        toast.error('Request timed out. Please check your connection and try again.');
      }
    } finally {
      // Always reset submitting state
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* Breadcrumb - Full breadcrumb path */}
      <BreadcrumbPortal>
        <Breadcrumb>
          <Breadcrumb.Item to="../cases">Cases</Breadcrumb.Item>
          <Breadcrumb.Item>Create</Breadcrumb.Item>
        </Breadcrumb>
      </BreadcrumbPortal>

      {/* Header Actions */}
      <HeaderActionsPortal>
        {isAutoFillEnabled && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!hasSavedData || isSubmitting}
            className="flex items-center gap-2"
            onClick={handleAutoFill}
          >
            <RotateCcw className="w-4 h-4" />
            Auto Fill
          </Button>
        )}
        <Button
          type="submit"
          variant="default"
          size="sm"
          disabled={isSubmitting || !selectedWorkflow || workflowsLoading || schemaLoading}
          className="flex items-center gap-2"
          onClick={handleSubmit}
        >
          <SendHorizonal className="w-4 h-4" />
          {isSubmitting ? 'Creating...' : 'Submit'}
        </Button>
      </HeaderActionsPortal>

      <div className="h-full flex flex-col overflow-hidden">
        {/* Form Content - Scrollable */}
        <div className="flex-1 overflow-y-auto bg-muted" style={{ height: 'calc(100% - 73px)' }}>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {workflowsLoading || schemaLoading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-foreground mx-auto mb-4"></div>
                <p className="text-muted-foreground">
                  {workflowsLoading ? 'Loading workflows...' : 'Loading form schema...'}
                </p>
              </div>
            ) : !selectedWorkflow ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground">No workflows available for this program.</p>
              </div>
            ) : formSchema.groups.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground">No form schema available for this workflow.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Metadata group rendered separately with RadioCardGroup */}
                {metadataGroup && (
                  <RadioCardGroup
                    group={metadataGroup}
                    formData={formData}
                    fieldErrors={fieldErrors}
                    disabled={isSubmitting}
                    onFieldChange={(fieldId, value) => {
                      handleInputChange(fieldId, value);
                      if (fieldErrors[fieldId]) {
                        setFieldErrors(prev => {
                          const newErrors = { ...prev };
                          delete newErrors[fieldId];
                          return newErrors;
                        });
                      }
                    }}
                  />
                )}

                {/* Other groups rendered in accordion */}
                <Accordion
                  type="single"
                  collapsible
                  value={activeGroup}
                  onValueChange={setActiveGroup}
                  className="space-y-4"
                >
                  {otherGroups.map((group, index) => (
                    <AccordionItem
                      key={index}
                      value={index.toString()}
                      className="bg-card rounded-xl border"
                      disabled={isSubmitting}
                    >
                      <AccordionTrigger className="px-6 py-4 text-lg font-medium hover:bg-muted rounded-lg data-[state=open]:rounded-b-none">
                        {group.label}
                      </AccordionTrigger>
                      <AccordionContent className="px-6 pb-6">
                        <div className="mt-4">
                          <FieldGroupRenderer
                            group={group}
                            formData={formData}
                            fieldErrors={fieldErrors}
                            disabled={isSubmitting}
                            onFieldChange={(fieldId, value) => {
                              handleInputChange(fieldId, value);
                              // Clear error when user starts typing
                              if (fieldErrors[fieldId]) {
                                setFieldErrors(prev => {
                                  const newErrors = { ...prev };
                                  delete newErrors[fieldId];
                                  return newErrors;
                                });
                              }
                            }}
                            onFieldBlur={handleInputBlur}
                          />
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </form>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
